/**
 * ContentLocaleSelect — the admin's CONTENT-LANGUAGE switcher (043 US3).
 *
 * It chooses which language's content the admin lists, creates into and
 * previews. It does NOT change the language the tool is labelled in: that is
 * the shell's own toggle, a different control for a different axis, and the two
 * are deliberately independent (`useContentLocale` carries the reasoning).
 *
 * WHY IT LIVES UNDER `components/cms` AND NOT IN THE SHELL: it is an
 * admin-content control, and the register scopes this concept to the CMS
 * surface. `AdminCmsLayout` renders it in the header band the same way it
 * already renders `ArchiveDrawer` from here, so the shell stays chrome and the
 * content axis stays owned by the CMS module. It must never appear on a member
 * surface, and being a CMS component is what makes that structural rather than
 * a rule someone has to remember.
 *
 * PROTO PARITY, and the ONE place it deliberately DIVERGES:
 *
 *   - Placement and order are the prototype's: the sticky header band's right
 *     side, first of the controls there, a 32px outline pill.
 *   - The option labels are the prototype's rendered strings, capital included.
 *     Finnish orthography lowercases language names and this control followed
 *     that rule first; the owner ruled the switcher matches the prototype
 *     instead, so the capital is now the decided copy (the Finnish message
 *     file records the reversal).
 *   - It carries an ACCESSIBLE NAME. The prototype's own switch has none -- no
 *     `aria-label`, no `aria-haspopup` -- which is a defect, not a target. This
 *     is the divergence.
 *
 * The option set comes from `CONTENT_LOCALES`, which is held to the wire
 * contract by `satisfies`, so this control cannot offer a value the api would
 * refuse.
 */

import type { JSX } from 'react';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { Select } from '../../primitives/Select.js';
import {
  CONTENT_LOCALES,
  type ContentLocale,
  toContentLocale,
  useContentLocale,
} from './useContentLocale.js';

/**
 * The option label per locale. A `Record` keyed by `ContentLocale` rather than
 * a template-built key, so a locale the wire starts admitting fails to
 * typecheck here instead of rendering a missing-message placeholder.
 */
const OPTION_LABEL_KEYS: Record<ContentLocale, MessageKeys> = {
  fi: 'cms.contentLocale.option.fi',
  sv: 'cms.contentLocale.option.sv',
  en: 'cms.contentLocale.option.en',
};

export function ContentLocaleSelect(): JSX.Element {
  const t = useTranslate();
  const { contentLocale, onContentLocaleChange } = useContentLocale();

  return (
    <Select.Root
      value={contentLocale}
      onValueChange={(next) => {
        // Radix hands back a bare string, so it goes through the SAME parser the
        // URL does. That is the point: the degradation rule then exists ONCE.
        // This line previously re-implemented it and fell back to the CURRENT
        // selection, so an unexpected value would have kept the old locale
        // while the comment claimed it reset to the default -- the two paths
        // had drifted apart, which is exactly what a second copy of a rule is
        // for. Radix only ever emits the values of the items rendered below, so
        // no reachable input exercises the fallback here; it is routed through
        // the parser to keep one rule rather than to guard a live case.
        onContentLocaleChange(toContentLocale(next));
      }}
    >
      <Select.Trigger
        size="xs"
        variant="pill"
        // The accessible name the prototype's switch lacks. It is the register
        // term's Finnish label, so the control announces the subject ("content
        // language") rather than its mechanism.
        aria-label={t('cms.contentLocale.label')}
        data-testid="cms-content-locale-select"
      >
        <Select.Value />
      </Select.Trigger>
      <Select.Content widthMode="fit">
        {CONTENT_LOCALES.map((locale) => (
          <Select.Item
            key={locale}
            value={locale}
            // 32px left, 8px right, the prototype's figures: the three labels
            // share one left edge and the row lands at its 118px width.
            inset="narrow"
            data-testid={`cms-content-locale-option-${locale}`}
          >
            {t(OPTION_LABEL_KEYS[locale])}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
}
